import pino from 'pino'

export const loggingConfig = {
  development: {
    level: 'debug',
    pretty: true,
    redact: [] as string[],
  },
  staging: {
    level: 'debug',
    pretty: false,
    redact: ['password', 'token', 'apiKey'] as string[],
  },
  production: {
    level: process.env.LOG_LEVEL || 'info',
    pretty: false,
    redact: ['password', 'token', 'apiKey', 'email', 'phone'] as string[],
  },
} as const

const config = loggingConfig[process.env.NODE_ENV as keyof typeof loggingConfig] || loggingConfig.development

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

// Create a Next.js compatible logger configuration
const createLogger = () => {
  // In browser environment, return a CORS-safe console-only logger
  if (isBrowser) {
    return {
      info: (data: any) => {
        // Only log to console in development, avoid any network calls that could cause CORS
        if (process.env.NODE_ENV === 'development') {
          console.log('[INFO]', typeof data === 'object' ? JSON.stringify(data, null, 2) : data)
        }
      },
      warn: (data: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WARN]', typeof data === 'object' ? JSON.stringify(data, null, 2) : data)
        }
      },
      error: (data: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[ERROR]', typeof data === 'object' ? JSON.stringify(data, null, 2) : data)
        }
      },
      debug: (data: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[DEBUG]', typeof data === 'object' ? JSON.stringify(data, null, 2) : data)
        }
      },
    }
  }

  // Check if we're in Edge Runtime (where pino.destination is not available)
  const isEdgeRuntime = typeof (globalThis as any).EdgeRuntime !== 'undefined' ||
    (typeof process !== 'undefined' && process.env.NEXT_RUNTIME === 'edge') ||
    (typeof process !== 'undefined' && !process.versions?.node)

  // Server-side configuration
  const baseConfig = {
    level: config.level,
    formatters: {
      level: (label: string) => ({ level: label }),
      bindings: (bindings: any) => ({
        pid: bindings.pid,
        hostname: bindings.hostname,
        node_version: process.version,
      }),
    },
    timestamp: pino.stdTimeFunctions.isoTime,
    redact: {
      paths: config.redact,
      censor: '[REDACTED]',
    },
  }

  // In Edge Runtime, always use basic pino without destination
  if (isEdgeRuntime) {
    return pino(baseConfig)
  }

  // In development, use pino-pretty synchronously to avoid worker thread issues
  if (config.pretty && process.env.NODE_ENV === 'development') {
    try {
      return pino(baseConfig, pino.destination({
        sync: true,
        dest: 1, // stdout
      }))
    } catch (error) {
      // Fallback to basic pino if destination fails
      console.warn('Failed to create pino destination, using basic logger:', error)
      return pino(baseConfig)
    }
  }

  // For production/staging, use basic configuration without transport
  return pino(baseConfig)
}

export const logger = createLogger()
